// CRAP is inflated only when the CI fallow coverage step times out (0% scoring, see
// .github/workflows/fallow.yml); these branchy fns are fully unit-tested. Drop when fixed.
// fallow-ignore-file complexity
// @plasser/ui — AssignmentCell: a table/detail cell showing the current assignee
// with an inline affordance to (re)assign via a picker. Generic over the assignee
// shape; the host wires the actual picker (e.g. via useFlowSurface + EntityPicker).

import * as React from "react";
import { UserPlus } from "lucide-react";

import { EntityChip } from "./EntityChip";
import { cn } from "../lib/utils";

export interface AssignmentValue {
  id: string;
  name: string;
  icon?: React.ReactNode;
}

export interface AssignmentCellProps {
  value?: AssignmentValue | null;
  /** Open the assignment picker. */
  onAssign: () => void;
  onClear?: () => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export function AssignmentCell({
  value,
  onAssign,
  onClear,
  placeholder = "Nicht zugewiesen",
  disabled,
  className,
}: AssignmentCellProps): React.ReactElement {
  if (value) {
    return (
      <span
        className={cn("inline-flex items-center gap-1", className)}
        data-testid="assignment-cell"
      >
        <EntityChip
          label={value.name}
          icon={value.icon}
          tone="primary"
          onClick={disabled ? undefined : onAssign}
          onRemove={onClear && !disabled ? onClear : undefined}
        />
      </span>
    );
  }
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onAssign}
      data-testid="assignment-cell-empty"
      // DS v3 / R3 (TES-2254): the empty "pick" affordance is a solid SECONDARY ACTION
      // on the one control radius (rounded-md, identical to Button/Input/Select) — not a
      // dashed rounded-full placeholder that reads as an empty field. Shares the canonical
      // focus ring (ring-ring/ring-offset-2) with Button/Select so focus states are
      // consistent across controls. Colour tokens unchanged (border-input/primary/accent).
      className={cn(
        "inline-flex items-center gap-1 rounded-md border border-input bg-background px-2.5 py-1 text-xs font-medium text-muted-foreground ring-offset-background transition-colors hover:border-primary hover:bg-accent hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60",
        className,
      )}
    >
      <UserPlus className="size-3" />
      {placeholder}
    </button>
  );
}
